import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AppV2 } from './AppV2Routes';
import { PlatformProtectedRoute } from './features/platform/PlatformProtectedRoute';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { ApiError } from './features/auth/types';

// Store
import { useAuthStore } from './stores/authStore';

// Components
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import AuthInitializer from './components/AuthInitializer';

// Pages - Lazy loaded for better performance

// Dashboard & Analytics
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const TodaysPulsePage = React.lazy(() => import('./pages/dashboard/TodaysPulsePage'));
// LeadsDashboardPage moved to /app/leads/dashboard (Go-backed) — see AppV2Routes.tsx.
// dashboard/attendance, dashboard/admissions, dashboard/hr, dashboard/analytics
// (Slice 5, docs/design/completeness-lld.md) now redirect to their /app/*
// equivalents (attendance/dashboard, admissions, hr, analytics); their pages
// retired.

// analytics, reports (Slice 5) now redirect to /app/analytics and
// /app/leads/reports; their legacy pages retired.

// User Management
// users (Slice 5) now redirects to /app/admin/users; its legacy page retired.

// Attendance & Leave Management
// attendance, leaves (Slice 5) now redirect to /app/attendance/register and
// /app/leave; their legacy pages retired.

// Sales & CRM
// LeadsPage/LeadDetailsPage/LeadConfigPage moved to /app/leads(/...) —
// Go CRM Milestone 1, see AppV2Routes.tsx. Rewired in place to call apiV2
// (authV2Store Bearer token), so they can no longer render inside this
// legacy (useAuthStore) shell.
const ChatbotConfigPage = React.lazy(() => import('./pages/chatbot/ChatbotConfigPage'));
// TasksPage (legacy Fastify CRM task board) retired — Slice 3
// (docs/design/completeness-lld.md "Slice 3") replaces it with the Go-backed
// features/crm tasks surface mounted at /app/leads/tasks (AppV2Routes.tsx).

// PR Management
// tickets, tickets/:id (Slice 5) now redirect to /app/tickets(/:id); their
// legacy pages retired.

// Automation & Rule Engine
const RuleBuilderPage = React.lazy(() => import('./pages/automation/RuleBuilderPage'));
const TemplateManagerPage = React.lazy(() => import('./pages/automation/TemplateManagerPage'));
const RuleAnalyticsPage = React.lazy(() => import('./pages/automation/RuleAnalyticsPage'));

// School Management
// SchoolManagementPage still renders school-management/grades and
// school-management/academic-progression (Slice 5 left these two alone —
// see the route comments below for why); the bare school-management and
// school-management/students routes now redirect and their pages retired.
const SchoolManagementPage = React.lazy(() => import('./pages/school/SchoolManagementPage'));
// schools, students, classes (Slice 5) now redirect to /app/settings,
// /app/students, /app/academic; their legacy pages retired.

// HR & Payroll — the legacy Workwave pages (pages/hr, pages/payroll) hit
// nonexistent Fastify endpoints and are retired; /hr and /payroll now redirect
// to the Go-backed console at /app/hr (docs/design/hr-payroll-web-lld.md §5).

// Settings
// admin/holidays, admin/users (Slice 5) now redirect to /app/calendar and
// /app/admin/users; their legacy pages retired.

// Error Pages
const NotFoundPage = React.lazy(() => import('./pages/common/NotFoundPage'));
const UnauthorizedPage = React.lazy(() => import('./pages/common/UnauthorizedPage'));

// Parent Pages
const ParentRedirectPage = React.lazy(() => import('./pages/parent/ParentRedirectPage'));

// Platform (system) admin console — Phase B, Go-backed, wholly separate auth
// surface from the org app (docs/design/platform-admin-persona-lld.md).
const PlatformLoginPage = React.lazy(() => import('./features/platform/PlatformLoginPage'));
const PlatformConsolePage = React.lazy(() => import('./features/platform/PlatformConsolePage'));

// Create QueryClient with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry ApiError responses that are client-side errors.
        if (
          error instanceof ApiError &&
          [401, 403, 404, 422].includes(error.status)
        ) {
          return false;
        }
        // Legacy: also guard legacy axios errors with response status.
        const legacyStatus = (error as { response?: { status?: number } })?.response?.status;
        if (legacyStatus === 401 || legacyStatus === 403) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Dashboard Wrapper Component - Routes users to appropriate dashboard
const DashboardWrapper: React.FC = () => {
  const { user } = useAuthStore();
  
  // If user is a parent, show the mobile app redirect page
  if (user?.role === 'PARENT' || (user?.roles && user.roles.includes('PARENT'))) {
    return <ParentRedirectPage />;
  }
  
  // Route users to their primary dashboard based on role
  if (user?.role) {
    // Sales roles - redirect to Today's Pulse dashboard for daily overview
    if (['SALES_MANAGER', 'SALES_REP'].includes(user.role)) {
      return <Navigate to="/dashboard/todays-pulse" replace />;
    }
    
    // HR roles - redirect to HR dashboard
    if (user.role === 'HR_MANAGER') {
      return <Navigate to="/dashboard/hr" replace />;
    }
    
    // Principal - redirect to admissions dashboard
    if (user.role === 'PRINCIPAL') {
      return <Navigate to="/dashboard/admissions" replace />;
    }
    
    // Org Head and Tenant Head - redirect to analytics dashboard for overview
    if (['ORG_HEAD', 'TENANT_HEAD'].includes(user.role)) {
      return <Navigate to="/dashboard/analytics" replace />;
    }
    
    // Super Admin - redirect to system user management
    if (user.role === 'SUPER_ADMIN') {
      return <Navigate to="/app/settings" replace />;
    }
  }
  
  // Fallback: show general dashboard page for other roles
  return <DashboardPage />;
};

// Protected Route Component with Role-based Access
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requiredRoles?: string[];
}> = React.memo(({ children, requiredRoles = [] }) => {
  const { isAuthenticated, isLoading, user, hasAnyRole, isHydrated } = useAuthStore();

  // Wait for hydration to complete
  if (!isHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles.length > 0 && user && !hasAnyRole(requiredRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
});

// Page Transition Wrapper
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  );
};

// Redirects a legacy /leads/:id bookmark to its Go-backed /app/leads/:id
// counterpart, preserving the id (a plain `<Navigate to="/app/leads/:id">`
// does not interpolate route params).
const LeadDetailRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/leads/${id}`} replace />;
};

// Redirects a legacy /tickets/:id bookmark to its Go-backed /app/tickets/:id
// counterpart, preserving the id (see LeadDetailRedirect above).
const TicketDetailRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/tickets/${id}`} replace />;
};

// Suspense Fallback
const SuspenseFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthInitializer />
          <div className="App">
            <AnimatePresence mode="wait">
              <Suspense fallback={<SuspenseFallback />}>
                <Routes>
                  {/* Public Routes */}
                  {/* Legacy /login retired -- /app/login (LoginV2Page) is the only login now. */}
                  <Route path="/login" element={<Navigate to="/app/login" replace />} />

                  {/* Error Routes */}
                  <Route
                    path="/unauthorized"
                    element={
                      <PageTransition>
                        <UnauthorizedPage />
                      </PageTransition>
                    }
                  />

                  {/* System User Management retired — redirect to Go console. */}
                  <Route path="/system-user" element={<Navigate to="/app/settings" replace />} />

                  {/* Protected Routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    
                    {/* Dashboard & Analytics */}
                    <Route
                      path="dashboard"
                      element={
                        <PageTransition>
                          <DashboardWrapper />
                        </PageTransition>
                      }
                    />
                    
                    {/* Role-based Dashboard Routes */}
                    <Route
                      path="dashboard/todays-pulse"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER', 'SALES_REP']}>
                          <PageTransition>
                            <TodaysPulsePage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Leads Dashboard moved to /app/leads/dashboard (Go CRM Milestone 1). */}
                    <Route path="dashboard/leads" element={<Navigate to="/app/leads/dashboard" replace />} />

                    {/* dashboard/attendance retired — redirect to the Go-backed
                        attendance dashboard (Slice 5, completeness-lld.md). */}
                    <Route path="dashboard/attendance" element={<Navigate to="/app/attendance/dashboard" replace />} />

                    {/* dashboard/admissions retired — redirect to the Go-backed
                        admissions console (Slice 5). */}
                    <Route path="dashboard/admissions" element={<Navigate to="/app/admissions" replace />} />

                    {/* dashboard/hr retired — redirect to the Go-backed HR
                        console (Slice 5). */}
                    <Route path="dashboard/hr" element={<Navigate to="/app/hr" replace />} />

                    {/* dashboard/analytics retired — redirect to the Go-backed
                        analytics dashboard (Slice 5). */}
                    <Route path="dashboard/analytics" element={<Navigate to="/app/analytics" replace />} />

                    {/* analytics retired — redirect to the Go-backed analytics
                        dashboard (Slice 5). */}
                    <Route path="analytics" element={<Navigate to="/app/analytics" replace />} />

                    {/* reports retired — redirect to the Go-backed sales report
                        (Slice 5). */}
                    <Route path="reports" element={<Navigate to="/app/leads/reports" replace />} />

                    {/* User Management */}
                    {/* users retired — redirect to the Go-backed admin/users
                        console (Slice 5). */}
                    <Route path="users" element={<Navigate to="/app/admin/users" replace />} />

                    {/* profile retired — the legacy page's change-password form
                        posted to PATCH /profile/password, an endpoint that
                        never existed on the Go backend, so it failed on every
                        submit. Redirect to the Go-backed self-service profile,
                        which carries the real change-password flow
                        (POST /auth/password). */}
                    <Route path="profile" element={<Navigate to="/app/my-profile" replace />} />

                    {/* Attendance & Leave Management */}
                    {/* attendance retired — redirect to the Go-backed section
                        register (Slice 5). */}
                    <Route path="attendance" element={<Navigate to="/app/attendance/register" replace />} />

                    {/* leaves retired — redirect to the Go-backed leave
                        console (Slice 5). */}
                    <Route path="leaves" element={<Navigate to="/app/leave" replace />} />

                    {/* Sales & CRM */}
                    {/* Leads/Lead Details moved to /app/leads(/:id) — Go CRM
                        Milestone 1, see AppV2Routes.tsx. */}
                    <Route path="leads" element={<Navigate to="/app/leads" replace />} />
                    <Route path="leads/:id" element={<LeadDetailRedirect />} />

                    {/* Lead Configuration moved to /app/leads/config — Go CRM Milestone 1. */}
                    <Route path="sales/config" element={<Navigate to="/app/leads/config" replace />} />

                    {/* WATI WhatsApp Configuration moved to the Go console. */}
                    <Route path="sales/chatbot-config" element={<Navigate to="/app/leads/wati" replace />} />
                    
                    <Route
                      path="sales/chatbot-config/rules/new"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleBuilderPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/rules/:id"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleBuilderPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/rules/:id/edit"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleBuilderPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/templates"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <TemplateManagerPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/templates/:id/edit"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <TemplateManagerPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/analytics"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <RuleAnalyticsPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="sales/chatbot-config/legacy"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'SALES_MANAGER']}>
                          <PageTransition>
                            <ChatbotConfigPage />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* /sales/tasks (legacy Fastify CRM task board) retired — Slice 3
                        (docs/design/completeness-lld.md "Slice 3") replaces it with
                        the Go-backed features/crm tasks surface at /app/leads/tasks
                        (AppV2Routes.tsx). The nav entry still points at /sales/tasks
                        (features/_shell/navConfig.ts) pending repoint by the main
                        loop, so redirect rather than 404 in the interim. */}
                    <Route path="sales/tasks" element={<Navigate to="/app/leads/tasks" replace />} />

                    {/* This route rendered LeadsPage as a placeholder pre-migration
                        (no dedicated call-log view existed); redirects to the
                        Go-backed leads list, where each lead's Call Logs tab lives now. */}
                    <Route path="sales/call-logs" element={<Navigate to="/app/leads" replace />} />

                    {/* PR Management - Parents use mobile app only.
                        tickets, tickets/:id retired — redirect to the
                        Go-backed tickets console (Slice 5). */}
                    <Route path="tickets" element={<Navigate to="/app/tickets" replace />} />
                    <Route path="tickets/:id" element={<TicketDetailRedirect />} />

                    {/* School Management.
                        school-management (bare) retired — redirect to the
                        Go-backed analytics dashboard (Slice 5). */}
                    <Route path="school-management" element={<Navigate to="/app/analytics" replace />} />

                    {/* Student Management Sub-pages.
                        school-management/students retired — redirect to the
                        Go-backed students list (Slice 5). */}
                    <Route path="school-management/students" element={<Navigate to="/app/students" replace />} />

                    {/* school-management/grades and school-management/academic-progression
                        LEFT UNCONVERTED (Slice 5): SchoolManagementPage's
                        defaultTab logic maps BOTH of these to the same
                        'grades' tab (GradeConfiguration) at mount — the
                        'progression'-tab content (AcademicProgression) is
                        reachable only by clicking within the page, not via
                        either route's initial render. Neither /app/academic
                        nor /app/sis/promotion is a confirmed 1:1 match for
                        what actually renders, so these stay on the legacy
                        page until the correct target is confirmed. */}
                    <Route
                      path="school-management/grades"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL']}>
                          <PageTransition>
                            <SchoolManagementPage defaultTab="grades" />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="school-management/academic-progression"
                      element={
                        <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_HEAD', 'TENANT_HEAD', 'PRINCIPAL']}>
                          <PageTransition>
                            <SchoolManagementPage defaultTab="progression" />
                          </PageTransition>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route path="school-management/notifications" element={<Navigate to="/app/settings" replace />} />

                    {/* schools retired — redirect to the Go-backed settings
                        console (schools tab) (Slice 5). */}
                    <Route path="schools" element={<Navigate to="/app/settings" replace />} />

                    {/* students retired — redirect to the Go-backed students
                        list (Slice 5). */}
                    <Route path="students" element={<Navigate to="/app/students" replace />} />

                    {/* classes retired — redirect to the Go-backed academic
                        structure console (Slice 5). */}
                    <Route path="classes" element={<Navigate to="/app/academic" replace />} />

                    {/* HR & Payroll — retired legacy pages; redirect to the
                        Go-backed console (docs/design/hr-payroll-web-lld.md §5). */}
                    <Route path="hr" element={<Navigate to="/app/hr" replace />} />
                    <Route path="payroll" element={<Navigate to="/app/hr" replace />} />

                    {/* Settings — retired legacy pages; redirect to the Go console. */}
                    <Route path="settings" element={<Navigate to="/app/settings" replace />} />
                    <Route path="settings/wati-config" element={<Navigate to="/app/leads/wati" replace />} />
                    
                    {/* Admin Routes.
                        admin/holidays retired — redirect to the Go-backed
                        calendar console (Slice 5). */}
                    <Route path="admin/holidays" element={<Navigate to="/app/calendar" replace />} />

                    {/* admin/users retired — redirect to the Go-backed
                        admin/users console (Slice 5). */}
                    <Route path="admin/users" element={<Navigate to="/app/admin/users" replace />} />
                  </Route>

                  {/* ---- Go API / SIS integration layer (/app/*) ---- */}
                  <Route path="/app/*" element={<AppV2 />} />

                  {/* ---- Platform (system) admin console — separate auth surface ---- */}
                  <Route
                    path="/platform/login"
                    element={
                      <PageTransition>
                        <PlatformLoginPage />
                      </PageTransition>
                    }
                  />
                  <Route
                    path="/platform"
                    element={
                      <PlatformProtectedRoute>
                        <PageTransition>
                          <PlatformConsolePage />
                        </PageTransition>
                      </PlatformProtectedRoute>
                    }
                  />

                  {/* 404 Route */}
                  <Route
                    path="*"
                    element={
                      <PageTransition>
                        <NotFoundPage />
                      </PageTransition>
                    }
                  />
                </Routes>
              </Suspense>
            </AnimatePresence>

            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#374151',
                  boxShadow: '0 4px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '16px',
                  fontSize: '14px',
                  maxWidth: '400px',
                },
                success: {
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #22c55e',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #ef4444',
                  },
                },
                loading: {
                  iconTheme: {
                    primary: '#6366f1',
                    secondary: '#fff',
                  },
                  style: {
                    borderLeft: '4px solid #6366f1',
                  },
                },
              }}
            />

            {/* React Query Devtools — pinned bottom-left so its floating
                toggle button (very high z-index) never sits under the
                bottom-right primary action button of a right-anchored
                AppDrawer (every drawer footer is `justify-end` against the
                right edge — see AppDrawer.tsx). */}
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
            )}
          </div>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
